import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Membershipschema, Userschema } from '@app/types/sdk';
import { AuthSessionExpiredError, useAuthSession } from '@app/context/auth-session';
import { useThemeMode } from '@app/context/theme';
import { OrgChangefeedProvider } from '@app/context/org-changefeed';
import { buildLoginRedirectPath } from '@app/utils/auth-redirect';
import { resolveErrorMessage } from '@app/utils/errors';
import {
  buildManualOrgSwitchLocation,
  buildOrgScopedPath,
  canonicalizeOrgLocation,
  isOrgScopedPath
} from '@app/utils/org-routes';
import {
  ActivityIcon,
  BellIcon,
  ChatIcon,
  CogIcon,
  ExitIcon,
  HandshakeIcon,
  HelpIcon,
  HomeIcon,
  GraphIcon,
  PathTraceIcon,
  ChartIcon,
  KeyIcon,
  LayersIcon,
  LinkIcon,
  MoonIcon,
  NetworkIcon,
  RadarIcon,
  ShieldIcon,
  SunIcon,
  UserIcon,
  UsersIcon,
  UploadIcon,
  TerminalIcon,
  CheckCircleIcon
} from '@app/components/icons';

type UIMembership = Membershipschema & {
  orgName?: string | null;
  rolesResolved?: Array<{ id?: string | null; slug?: string | null; name?: string | null }>;
  status?: string;
};

interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: ReactNode;
  description: string;
}

interface ManualOrgSwitchIntent {
  requestId: number;
  sourceOrgId: string | null;
  targetOrgId: string;
}

const getResponseStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const response = (error as { response?: unknown }).response;
  return response instanceof Response ? response.status : null;
};

const NAV_ITEMS: NavItem[] = [
  { key: 'overview', label: 'Overview', to: 'overview', icon: <HomeIcon size={18} />, description: 'Org snapshot & shortcuts' },
  {
    key: 'incidents',
    label: 'Incidents',
    to: 'incidents',
    icon: <BellIcon size={18} />,
    description: 'Active incident workflow and history'
  },
  {
    key: 'grafana',
    label: 'Telemetry',
    to: 'grafana',
    icon: <ChartIcon size={18} />,
    description: 'Power-user telemetry explorer'
  },
  {
    key: 'ping-mesh',
    label: 'Ping Mesh',
    to: 'ping-mesh',
    icon: <GraphIcon size={18} />,
    description: 'Site-to-site reachability and latency'
  },
  {
    key: 'flows',
    label: 'Flow Analytics',
    to: 'flows',
    icon: <ActivityIcon size={18} />,
    description: 'Observed network traffic, sources, and conversations'
  },
  {
    key: 'chat',
    label: 'Chat',
    to: 'chat-agents',
    icon: <ChatIcon size={18} />,
    description: 'Threads, runs, artifacts'
  },
  {
    key: 'runbooks',
    label: 'Runbooks',
    to: 'runbooks',
    icon: <HandshakeIcon size={18} />,
    description: 'Templates, versions, publishing'
  },
  {
    key: 'changes',
    label: 'Change Management',
    to: 'changes',
    icon: <CheckCircleIcon size={18} />,
    description: 'Pre/post validation and evidence'
  },
  {
    key: 'people-access',
    label: 'Access & Identity',
    to: 'people-access',
    icon: <UsersIcon size={18} />,
    description: 'Members, API keys, roles'
  },
  {
    key: 'connectivity',
    label: 'Connectivity & Sites',
    to: 'connectivity',
    icon: <NetworkIcon size={18} />,
    description: 'Sites, clusters, connectors'
  },
  {
    key: 'posture',
    label: 'Network Posture',
    to: 'posture',
    icon: <ShieldIcon size={18} />,
    description: 'Strengths, weaknesses, and actionable posture outcomes'
  },
  {
    key: 'topology-v2',
    label: 'Topology v2',
    to: 'topology-v2',
    icon: <GraphIcon size={18} />,
    description: 'Topology views catalog + resolve'
  },
  {
    key: 'path-tracer',
    label: 'Path Tracer',
    to: 'path-tracer',
    icon: <PathTraceIcon size={18} />,
    description: 'Direct source/destination path tracing'
  },
  {
    key: 'inventory',
    label: 'Inventory',
    to: 'inventory',
    icon: <LayersIcon size={18} />,
    description: 'Org-wide device inventory'
  },
  {
    key: 'troubleshoot',
    label: 'Troubleshooting Utilities',
    to: 'troubleshoot',
    icon: <TerminalIcon size={18} />,
    description: 'Ping, traceroute, PCAP and diagnostics'
  },
  {
    key: 'endpoints',
    label: 'Endpoints',
    to: 'endpoints',
    icon: <RadarIcon size={18} />,
    description: 'Endpoint attachment and enrichment'
  },
  {
    key: 'cve',
    label: 'CVEs',
    to: 'cve',
    icon: <ShieldIcon size={18} />,
    description: 'Real-time vulnerability assessments'
  },
  {
    key: 'credentials',
    label: 'Credentials',
    to: 'credentials',
    icon: <KeyIcon size={18} />,
    description: 'Credential store & assignments'
  },
  {
    key: 'integrations',
    label: 'Integrations',
    to: 'integrations',
    icon: <LinkIcon size={18} />,
    description: 'ServiceNow and external systems'
  },
  {
    key: 'knowledge',
    label: 'Knowledge',
    to: 'knowledge',
    icon: <UploadIcon size={18} />,
    description: 'Send PDFs to the knowledge base'
  },
  {
    key: 'api-docs',
    label: 'API Docs',
    to: 'api-docs',
    icon: <HelpIcon size={18} />,
    description: 'OpenAPI contract and developer references'
  },
  {
    key: 'audit',
    label: 'Audit & Activity',
    to: 'audit-activity',
    icon: <ActivityIcon size={18} />,
    description: 'Event trails & filters'
  },
  {
    key: 'settings',
    label: 'Org Settings',
    to: 'settings',
    icon: <CogIcon size={18} />,
    description: 'Organization profile'
  }
];

  const ShellPage = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();
  const shellLocationState = (location.state as { accessFallbackMessage?: unknown } | null) ?? null;
  const isEmbedded = useMemo(() => new URLSearchParams(location.search).get('embed') === '1', [location.search]);
  const { user, tokens, loading, clearSession, switchOrganization } = useAuthSession();
  const { mode: themeMode, toggleMode: toggleThemeMode } = useThemeMode();

  const profileUser = useMemo(() => (user?.user as (Userschema & { displayName?: string | null }) | undefined), [user]);
  const [orgSwitching, setOrgSwitching] = useState(false);
  const [routeOrgSwitching, setRouteOrgSwitching] = useState(false);
  const [orgSwitchError, setOrgSwitchError] = useState<string | null>(null);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    try {
      const stored = window.localStorage.getItem('reap.orgNavCollapsed');
      if (stored == null) {
        return true;
      }
      return stored === 'true';
    } catch {
      return true;
    }
  });

  const orgMenuRef = useRef<HTMLDivElement | null>(null);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const routeOrgSwitchIntentRef = useRef<string | null>(null);
  const manualOrgSwitchIntentRef = useRef<ManualOrgSwitchIntent | null>(null);
  const orgSwitchRequestIdRef = useRef(0);

  const memberships = useMemo(() => (user?.memberships ?? []) as UIMembership[], [user]);
  const requestedOrgId = useMemo(() => {
    const value = new URLSearchParams(location.search).get('orgId')?.trim();
    return value ? value : null;
  }, [location.search]);
  const activeOrgId = user?.activeOrg?.orgId ?? memberships[0]?.orgId ?? null;
  const manualOrgSwitchIntent = manualOrgSwitchIntentRef.current;
  const routeOrgMismatch = Boolean(requestedOrgId && activeOrgId && requestedOrgId !== activeOrgId);
  const blockOrgContent =
    routeOrgMismatch || routeOrgSwitching || Boolean(manualOrgSwitchIntent && manualOrgSwitchIntent.sourceOrgId !== activeOrgId);
  const activeMembership = memberships.find((membership) => membership.orgId === activeOrgId) ?? null;
  const requestedOrgMembership = requestedOrgId ? memberships.find((membership) => membership.orgId === requestedOrgId) ?? null : null;
  const fallbackMembership = memberships[0] ?? null;
  const accessFallbackMessage =
    typeof shellLocationState?.accessFallbackMessage === 'string' ? shellLocationState.accessFallbackMessage : null;

  const formatOrgDisplayName = useCallback(
    (membership: UIMembership | null | undefined): string | null => {
      if (!membership) {
        return null;
      }
      const name = membership.orgName?.trim();
      if (name && name.length > 0) {
        return name;
      }
      const slug = membership.orgSlug?.trim();
      if (slug && slug.length > 0) {
        return slug;
      }
      if (membership.orgId) {
        return `Org ${membership.orgId.slice(0, 8)}`;
      }
      return null;
    },
    []
  );
  const activeOrgName =
    formatOrgDisplayName(activeMembership) ??
    formatOrgDisplayName(fallbackMembership) ??
    (memberships.length === 0 ? 'Select organization' : 'Organization');

  const fallbackFromRequestedOrg = useCallback(
    (accessFallbackMessage: string) => {
      if (!activeOrgId) {
        return;
      }
      const nextLocation = buildManualOrgSwitchLocation(location, activeOrgId);
      navigate(
        nextLocation,
        {
          replace: true,
          state: { accessFallbackMessage }
        }
      );
    },
    [activeOrgId, location, navigate]
  );

  useEffect(() => {
    if (!tokens) {
      navigate(buildLoginRedirectPath(location, { orgId: requestedOrgId }), { replace: true });
    }
  }, [location, navigate, requestedOrgId, tokens]);

  useEffect(() => {
    if (!user || !activeOrgId || requestedOrgId || !isOrgScopedPath(location.pathname) || manualOrgSwitchIntentRef.current) {
      return;
    }
    navigate(canonicalizeOrgLocation(location, activeOrgId), {
      replace: true,
      state: location.state
    });
  }, [activeOrgId, location, navigate, requestedOrgId, user]);

  useEffect(() => {
    if (!user || !requestedOrgId) {
      return;
    }
    if (memberships.some((membership) => membership.orgId === requestedOrgId)) {
      return;
    }

    fallbackFromRequestedOrg('We couldn’t open the requested organization, so we took you to an organization you can access.');
  }, [fallbackFromRequestedOrg, memberships, requestedOrgId, user]);

  useEffect(() => {
    if (manualOrgSwitchIntentRef.current) {
      return;
    }
    if (!user || !requestedOrgId || requestedOrgId === activeOrgId || !requestedOrgMembership) {
      setRouteOrgSwitching(false);
      setOrgSwitching(false);
      routeOrgSwitchIntentRef.current = null;
      return;
    }
    if (!tokens?.refreshToken) {
      setRouteOrgSwitching(false);
      setOrgSwitching(false);
      routeOrgSwitchIntentRef.current = null;
      navigate(buildLoginRedirectPath(location, { orgId: requestedOrgId }), { replace: true });
      return;
    }

    const switchIntent = `${location.pathname}${location.search}${location.hash}:${requestedOrgId}`;
    if (routeOrgSwitchIntentRef.current === switchIntent) {
      return;
    }
    routeOrgSwitchIntentRef.current = switchIntent;

    let cancelled = false;
    setRouteOrgSwitching(true);
    setOrgSwitching(true);
    setOrgSwitchError(null);
    setOrgMenuOpen(false);

    void (async () => {
      try {
        await switchOrganization(requestedOrgId);
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof AuthSessionExpiredError) {
          routeOrgSwitchIntentRef.current = null;
          navigate(buildLoginRedirectPath(location, { orgId: requestedOrgId }), { replace: true });
          return;
        }

        if (getResponseStatus(error) === 403) {
          routeOrgSwitchIntentRef.current = null;
          fallbackFromRequestedOrg('We couldn’t open the requested organization, so we took you to an organization you can access.');
          return;
        }

        const message = await resolveErrorMessage(error, 'We could not switch organizations. Refresh to try again.');
        if (!cancelled) {
          routeOrgSwitchIntentRef.current = null;
          setOrgSwitchError(message);
        }
      } finally {
        if (!cancelled) {
          setRouteOrgSwitching(false);
          setOrgSwitching(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeOrgId,
    fallbackFromRequestedOrg,
    location,
    navigate,
    requestedOrgId,
    requestedOrgMembership,
    switchOrganization,
    tokens?.refreshToken,
    user
  ]);

  const handleOrgChange = useCallback(
    async (orgId: string) => {
      if (!tokens?.refreshToken || orgId === activeOrgId) {
        return;
      }

      const requestId = orgSwitchRequestIdRef.current + 1;
      orgSwitchRequestIdRef.current = requestId;
      manualOrgSwitchIntentRef.current = {
        requestId,
        sourceOrgId: activeOrgId,
        targetOrgId: orgId
      };
      routeOrgSwitchIntentRef.current = null;

      setOrgSwitching(true);
      setOrgSwitchError(null);

      try {
        await switchOrganization(orgId);
        const currentIntent = manualOrgSwitchIntentRef.current;
        if (!currentIntent || currentIntent.requestId !== requestId || currentIntent.targetOrgId !== orgId) {
          return;
        }
        const cleanupLocation = buildManualOrgSwitchLocation(location, orgId);
        navigate(cleanupLocation, { replace: true, state: null });
        manualOrgSwitchIntentRef.current = null;
        setOrgSwitching(false);
      } catch (error) {
        const currentIntent = manualOrgSwitchIntentRef.current;
        if (!currentIntent || currentIntent.requestId !== requestId) {
          return;
        }
        manualOrgSwitchIntentRef.current = null;
        setOrgSwitching(false);
        if (error instanceof AuthSessionExpiredError) {
          navigate(buildLoginRedirectPath(location, { orgId: requestedOrgId }), { replace: true });
          return;
        }

        const message = await resolveErrorMessage(error, 'We could not switch organizations. Try again in a moment.');
        setOrgSwitchError(message);
      }
    },
    [activeOrgId, location, navigate, switchOrganization, tokens?.refreshToken]
  );

  useEffect(() => {
    if (!orgMenuOpen && !profileMenuOpen) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (orgMenuOpen && orgMenuRef.current && !orgMenuRef.current.contains(target)) {
        setOrgMenuOpen(false);
      }
      if (profileMenuOpen && profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        setProfileMenuOpen(false);
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOrgMenuOpen(false);
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [orgMenuOpen, profileMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem('reap.orgNavCollapsed', String(navCollapsed));
    } catch {
      // ignore storage failures (private browsing, etc.)
    }
  }, [navCollapsed]);

  const handleOrgMenuToggle = useCallback(() => {
    setOrgMenuOpen((previous) => !previous);
    setProfileMenuOpen(false);
  }, []);

  const handleOrgMenuSelect = useCallback(
    (orgId: string) => {
      setOrgMenuOpen(false);
      void handleOrgChange(orgId);
    },
    [handleOrgChange]
  );

  const handleOrgMenuCreate = useCallback(() => {
    setOrgMenuOpen(false);
    navigate('/orgs/create');
  }, [navigate]);

  const handleProfileToggle = useCallback(() => {
    setProfileMenuOpen((previous) => !previous);
    setOrgMenuOpen(false);
  }, []);

  const handleProfileNavigate = useCallback(() => {
    setProfileMenuOpen(false);
    navigate('/profile');
  }, [navigate]);

  const handleHelp = useCallback(() => {
    navigate(activeOrgId ? buildOrgScopedPath('/app/api-docs', activeOrgId) : '/app/api-docs');
  }, [activeOrgId, navigate]);

  const handleSignOut = useCallback(() => {
    clearSession();
    navigate('/login', { replace: true });
  }, [clearSession, navigate]);

  const activeNav = useMemo(() => {
    const pathname = location.pathname.replace(/\/+$/, '');
    return (
      NAV_ITEMS.find((item) => {
        const target = `/app/${item.to}`.replace(/\/+$/, '');
        return pathname === target || pathname.startsWith(`${target}/`);
      }) ?? null
    );
  }, [location.pathname]);

  if (!tokens) {
    return <Navigate to={buildLoginRedirectPath(location, { orgId: requestedOrgId })} replace />;
  }

  if (loading || !user) {
    return (
      <main className="org-shell org-shell--loading">
        <section className="org-shell__main">
          <p>Loading your workspace…</p>
        </section>
      </main>
    );
  }

  if (isEmbedded) {
    return (
      <main className="org-shell org-shell--embedded">
        <section className="org-shell__main">
          {accessFallbackMessage && (
            <p className="org-shell__banner" role="alert">
              {accessFallbackMessage}
            </p>
          )}
          {orgSwitchError && (
            <p className="org-shell__banner" role="alert">
              {orgSwitchError}
            </p>
          )}
          {blockOrgContent ? (
            orgSwitchError ? null : <p className="org-shell__banner">Switching organization…</p>
          ) : (
            <OrgChangefeedProvider key={activeOrgId ?? 'no-org'} orgId={activeOrgId}>
              <Outlet />
            </OrgChangefeedProvider>
          )}
          {children}
        </section>
      </main>
    );
  }

  return (
    <main className="org-shell">
      <header className="org-shell__appbar">
        <div className="appbar__brand">
          <img
            src={themeMode === 'dark' ? '/assets/reapitnow_logo_white.png' : '/assets/reapitnow_logo.png'}
            alt="REAP"
            className="appbar__logo"
          />
          <span className="appbar__title">Organization</span>
        </div>

        <div className="appbar__org" ref={orgMenuRef}>
          <button
            type="button"
            className="appbar__org-trigger"
            onClick={handleOrgMenuToggle}
            disabled={memberships.length === 0}
            aria-haspopup="menu"
            aria-expanded={orgMenuOpen}
            title="Switch organization"
          >
            <span className="appbar__org-label">{activeOrgName}</span>
            <span className="appbar__caret" aria-hidden="true" />
          </button>

          {orgMenuOpen && (
            <div className="appbar__org-menu" role="menu">
              {memberships.length === 0 ? (
                <div className="appbar__org-empty">No organizations available.</div>
              ) : (
                memberships.map((membership) => {
                  const isActive = membership.orgId === activeOrgId;
                  return (
                    <button
                      key={membership.orgId ?? membership.id}
                      type="button"
                      className={`appbar__org-item${isActive ? ' appbar__org-item--active' : ''}`}
                      onClick={() => handleOrgMenuSelect(membership.orgId ?? '')}
                      role="menuitem"
                      disabled={orgSwitching}
                    >
                      <span className="appbar__org-name">{formatOrgDisplayName(membership) ?? 'Organization'}</span>
                      {isActive && <span className="appbar__org-badge">Active</span>}
                    </button>
                  );
                })
              )}

              <div className="appbar__org-divider" role="separator" />
              <button type="button" className="appbar__org-item appbar__org-item--action" onClick={handleOrgMenuCreate} role="menuitem">
                + Create new organization…
              </button>
            </div>
          )}
          {orgSwitching && <span className="appbar__org-status">Switching…</span>}
        </div>

        <div className="appbar__actions">
          <button type="button" className="icon-button" onClick={toggleThemeMode} aria-label="Toggle theme">
            {themeMode === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
          </button>
          <button type="button" className="icon-button" aria-label="Alerts">
            <BellIcon size={18} />
          </button>
          <button type="button" className="icon-button" aria-label="Help & documentation" onClick={handleHelp}>
            <HelpIcon size={18} />
          </button>
          <div className="appbar__profile" ref={profileMenuRef}>
            <button type="button" className="icon-button" onClick={handleProfileToggle} aria-haspopup="menu" aria-expanded={profileMenuOpen} title="Account menu">
              <UserIcon size={18} />
            </button>
            {profileMenuOpen && (
              <div className="appbar__profile-menu" role="menu">
                <div className="appbar__profile-summary">
                  <strong>{profileUser?.displayName ?? profileUser?.email ?? 'Account'}</strong>
                  <span>{memberships.length} org memberships</span>
                </div>
                <button type="button" className="appbar__profile-item" onClick={handleProfileNavigate} role="menuitem">
                  View profile
                </button>
                <button type="button" className="appbar__profile-item appbar__profile-item--danger" onClick={handleSignOut} role="menuitem">
                  <ExitIcon size={16} />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="org-shell__body">
        <nav className={`org-shell__nav${navCollapsed ? ' org-shell__nav--collapsed' : ''}`} aria-label="Primary">
          <div className="org-shell__nav-header">
            <div className="org-shell__nav-caption">Modules</div>
            <button
              type="button"
              className="org-shell__nav-toggle"
              aria-label={navCollapsed ? 'Expand modules navigation' : 'Collapse modules navigation'}
              onClick={() => setNavCollapsed((prev) => !prev)}>
              {navCollapsed ? '›' : '‹'}
            </button>
          </div>
          <ul>
            {NAV_ITEMS.map((item) => (
              <li key={item.key}>
                <NavLink
                  to={activeOrgId ? buildOrgScopedPath(`/app/${item.to}`, activeOrgId) : item.to}
                  className={({ isActive }) => `org-shell__nav-link${isActive ? ' org-shell__nav-link--active' : ''}`}
                  aria-label={navCollapsed ? item.label : undefined}
                  title={navCollapsed ? item.label : undefined}>
                  <span className="org-shell__nav-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="org-shell__nav-text">
                    <span className="org-shell__nav-label">{item.label}</span>
                    <span className="org-shell__nav-description">{item.description}</span>
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <section className="org-shell__main">
          {accessFallbackMessage && (
            <p className="org-shell__banner" role="alert">
              {accessFallbackMessage}
            </p>
          )}
          {orgSwitchError && (
            <p className="org-shell__banner" role="alert">
              {orgSwitchError}
            </p>
          )}

          {blockOrgContent ? (
            orgSwitchError ? null : <p className="org-shell__banner">Switching organization…</p>
          ) : (
            <OrgChangefeedProvider key={activeOrgId ?? 'no-org'} orgId={activeOrgId}>
              <Outlet />
            </OrgChangefeedProvider>
          )}
          {children}
        </section>
      </div>
    </main>
  );
};

export default ShellPage;
